import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { Calendar } from 'primereact/calendar';
import { MultiSelect } from 'primereact/multiselect';
import { RadioButton } from 'primereact/radiobutton';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import dayjs from 'dayjs';
import {
  StanjeRobeBrand,
  StanjeRobeCurrentStatusRow,
  StanjeRobeProduct,
  StanjeRobeStockInRecord,
  StanjeRobeStockOutRecord
} from '../types';
import {
  stanjeRobeBrandsAPI,
  stanjeRobeProductsAPI,
  stanjeRobeStatusAPI,
  stanjeRobeStockInAPI,
  stanjeRobeStockOutAPI
} from '../services/api';
import { escapeHtml, openPrintWindow } from '../utils/printableReport';
import { formatM2, formatStanjeRobeDate } from '../utils/stanjeRobeCalc';

type ReportType = 'all' | 'stock_in' | 'stock_out' | 'current_status';

const formatDateSr = (d: Date) => d.toLocaleDateString('sr-RS');

const formatUnit = (unit?: string) => {
  if (unit === 'm2') return 'm²';
  if (unit === 'paleta') return 'paleta';
  if (unit === 'kom') return 'kom';
  return unit || '';
};

const recordDate = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : formatStanjeRobeDate(iso);
};

const resolveBrandId = (brandId?: string | { _id?: string } | null) =>
  typeof brandId === 'string' ? brandId : brandId?._id || '';

const appendToBody = typeof document !== 'undefined' ? document.body : undefined;

const StanjeRobeReports: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [brands, setBrands] = useState<StanjeRobeBrand[]>([]);
  const [products, setProducts] = useState<StanjeRobeProduct[]>([]);
  const [stockIn, setStockIn] = useState<StanjeRobeStockInRecord[]>([]);
  const [stockOut, setStockOut] = useState<StanjeRobeStockOutRecord[]>([]);
  const [currentRows, setCurrentRows] = useState<StanjeRobeCurrentStatusRow[]>([]);

  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [selectedBrandIds, setSelectedBrandIds] = useState<string[]>([]);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [reportType, setReportType] = useState<ReportType>('all');

  const brandOptions = useMemo(
    () => brands.map((b) => ({ label: b.name, value: b._id })),
    [brands]
  );

  const productOptions = useMemo(() => {
    const brandSet = new Set(selectedBrandIds);
    return products
      .filter((p) => !brandSet.size || brandSet.has(resolveBrandId(p.brandId)))
      .map((p) => ({ label: `${p.brandName} — ${p.name}`, value: p._id }));
  }, [products, selectedBrandIds]);

  const selectedBrandNames = useMemo(() => {
    const set = new Set(selectedBrandIds);
    return brands.filter((b) => set.has(b._id)).map((b) => b.name);
  }, [brands, selectedBrandIds]);

  const selectedProductNames = useMemo(() => {
    const set = new Set(selectedProductIds);
    return products.filter((p) => set.has(p._id)).map((p) => p.name);
  }, [products, selectedProductIds]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [b, p, ins, outs, status] = await Promise.all([
        stanjeRobeBrandsAPI.getAll(),
        stanjeRobeProductsAPI.getAll(),
        stanjeRobeStockInAPI.getAll({ showAll: true }),
        stanjeRobeStockOutAPI.getAll(),
        stanjeRobeStatusAPI.getCurrent()
      ]);
      setBrands(b);
      setProducts(p);
      setStockIn(ins);
      setStockOut(outs);
      setCurrentRows(status.rows || []);
    } catch (e) {
      console.error(e);
      setError('Greška pri dohvatanju podataka.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, []);

  const matchBrand = (brandId?: string) => {
    if (!selectedBrandIds.length) return true;
    return brandId ? selectedBrandIds.includes(String(brandId)) : false;
  };

  const matchProduct = (productId?: string) => {
    if (!selectedProductIds.length) return true;
    return productId ? selectedProductIds.includes(String(productId)) : false;
  };

  const inRange = (iso?: string, from?: dayjs.Dayjs, to?: dayjs.Dayjs) => {
    if (!iso || !from || !to) return false;
    const d = dayjs(iso);
    return d.valueOf() >= from.valueOf() && d.valueOf() <= to.valueOf();
  };

  const buildFiltered = () => {
    if (!fromDate || !toDate) {
      return { ins: [] as StanjeRobeStockInRecord[], outs: [] as StanjeRobeStockOutRecord[] };
    }
    const from = dayjs(fromDate).startOf('day');
    const to = dayjs(toDate).endOf('day');

    const ins = stockIn.filter(
      (r) => inRange(r.entryDate, from, to) && matchBrand(r.brandId) && matchProduct(r.productId)
    );
    const outs = stockOut.filter(
      (r) => inRange(r.outDate, from, to) && matchBrand(r.brandId) && matchProduct(r.productId)
    );
    return { ins, outs };
  };

  const filteredStatus = useMemo(
    () =>
      currentRows.filter(
        (r) => matchBrand(r.brandId) && matchProduct(r.productId)
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentRows, selectedBrandIds, selectedProductIds]
  );

  const handleGenerate = () => {
    setError('');

    const includeIn = reportType === 'all' || reportType === 'stock_in';
    const includeOut = reportType === 'all' || reportType === 'stock_out';
    const includeStatus = reportType === 'all' || reportType === 'current_status';
    const needsDateRange = includeIn || includeOut;

    if (needsDateRange) {
      if (!fromDate || !toDate) {
        setError('Početni i završni datum su obavezni.');
        return;
      }
      if (dayjs(toDate).isBefore(dayjs(fromDate), 'day')) {
        setError('Završni datum ne može biti pre početnog.');
        return;
      }
    }

    const { ins, outs } = needsDateRange
      ? buildFiltered()
      : { ins: [] as StanjeRobeStockInRecord[], outs: [] as StanjeRobeStockOutRecord[] };

    if (includeIn && !ins.length && reportType === 'stock_in') {
      setError('Nema ulaza na stanje za izabrani period i filtere.');
      return;
    }
    if (includeOut && !outs.length && reportType === 'stock_out') {
      setError('Nema izlaza sa stanja za izabrani period i filtere.');
      return;
    }
    if (includeStatus && !filteredStatus.length && reportType === 'current_status') {
      setError('Nema podataka o trenutnom stanju za izabrane filtere.');
      return;
    }
    if (reportType === 'all' && !ins.length && !outs.length && !filteredStatus.length) {
      setError('Nema podataka za izabrani period i filtere.');
      return;
    }

    const periodStr =
      needsDateRange && fromDate && toDate
        ? `${formatDateSr(fromDate)} - ${formatDateSr(toDate)}`
        : 'Trenutno stanje';
    const brandsStr = selectedBrandNames.length ? selectedBrandNames.join(', ') : 'Svi brendovi';
    const productsStr = selectedProductNames.length ? selectedProductNames.join(', ') : 'Sva roba';

    let body = `
      <h1>Izveštaji - Stanje robe</h1>
      <div class="meta">
        <div><strong>Period:</strong> ${escapeHtml(periodStr)}</div>
        <div><strong>Brendovi:</strong> ${escapeHtml(brandsStr)}</div>
        <div><strong>Roba:</strong> ${escapeHtml(productsStr)}</div>
        <div class="muted">Generisano: ${escapeHtml(new Date().toLocaleString('sr-RS'))}</div>
      </div>
    `;

    if (includeIn) {
      const totalM2 = ins.reduce((s, r) => s + Number(r.m2Total || 0), 0);
      body += `<h2>Ulaz na stanje</h2>
        <div class="meta"><div><strong>Ukupno m²:</strong> ${escapeHtml(formatM2(totalM2))}</div>
        <div><strong>Broj zapisa:</strong> ${escapeHtml(ins.length)}</div></div>`;

      if (!ins.length) {
        body += `<div class="meta muted">Nema ulaza za izabrani period.</div>`;
      } else {
        body += `<table>
          <thead>
            <tr>
              <th>Datum</th>
              <th>Brend</th>
              <th>Roba</th>
              <th class="right">Količina</th>
              <th>Jedinica</th>
              <th class="right">m²</th>
              <th>Napomena</th>
            </tr>
          </thead>
          <tbody>
            ${ins
              .slice()
              .sort((a, b) => (a.entryDate < b.entryDate ? 1 : a.entryDate > b.entryDate ? -1 : 0))
              .map(
                (r) => `<tr>
                  <td>${escapeHtml(recordDate(r.entryDate))}</td>
                  <td>${escapeHtml(r.brandName)}</td>
                  <td>${escapeHtml(r.productName)}</td>
                  <td class="right">${escapeHtml(String(r.quantity ?? ''))}</td>
                  <td>${escapeHtml(formatUnit(r.unit))}</td>
                  <td class="right">${escapeHtml(formatM2(r.m2Total ?? 0))}</td>
                  <td>${escapeHtml(r.notes || '')}</td>
                </tr>`
              )
              .join('')}
          </tbody>
        </table>`;
      }
    }

    if (includeOut) {
      const totalM2 = outs.reduce((s, r) => s + Number(r.squareMeters || 0), 0);
      body += `<h2>Skidanje sa stanja / isporuke</h2>
        <div class="meta"><div><strong>Ukupno m²:</strong> ${escapeHtml(formatM2(totalM2))}</div>
        <div><strong>Broj zapisa:</strong> ${escapeHtml(outs.length)}</div></div>`;

      if (!outs.length) {
        body += `<div class="meta muted">Nema izlaza za izabrani period.</div>`;
      } else {
        body += `<table>
          <thead>
            <tr>
              <th>Datum</th>
              <th>Brend</th>
              <th>Roba</th>
              <th>Kupac</th>
              <th class="right">Količina</th>
              <th>Jedinica</th>
              <th class="right">m²</th>
              <th>Napomena</th>
            </tr>
          </thead>
          <tbody>
            ${outs
              .slice()
              .sort((a, b) => (a.outDate < b.outDate ? 1 : a.outDate > b.outDate ? -1 : 0))
              .map((r) => {
                const customer =
                  r.customerName ||
                  (typeof r.customerId === 'object' ? r.customerId?.name : '') ||
                  '';
                return `<tr>
                  <td>${escapeHtml(recordDate(r.outDate))}</td>
                  <td>${escapeHtml(r.brandName)}</td>
                  <td>${escapeHtml(r.productName)}</td>
                  <td>${escapeHtml(customer)}</td>
                  <td class="right">${escapeHtml(String(r.quantity ?? ''))}</td>
                  <td>${escapeHtml(formatUnit(r.unit))}</td>
                  <td class="right">${escapeHtml(formatM2(r.squareMeters ?? 0))}</td>
                  <td>${escapeHtml(r.notes || '')}</td>
                </tr>`;
              })
              .join('')}
          </tbody>
        </table>`;
      }
    }

    if (includeStatus) {
      const totalM2 = filteredStatus.reduce((s, r) => s + Number(r.currentM2 || 0), 0);
      const totalKom = filteredStatus.reduce((s, r) => s + Number(r.currentKom || 0), 0);
      const totalPaleta = filteredStatus.reduce((s, r) => s + Number(r.currentPaleta || 0), 0);

      body += `<h2>Trenutno stanje</h2>
        <div class="meta">
          <div><strong>Stavki:</strong> ${escapeHtml(filteredStatus.length)}</div>
          <div><strong>Ukupno kom:</strong> ${escapeHtml(formatM2(totalKom))}</div>
          <div><strong>Ukupno paleta:</strong> ${escapeHtml(formatM2(totalPaleta))}</div>
          <div><strong>Ukupno m²:</strong> ${escapeHtml(formatM2(totalM2))}</div>
        </div>`;

      if (!filteredStatus.length) {
        body += `<div class="meta muted">Nema podataka o stanju.</div>`;
      } else {
        body += `<table>
          <thead>
            <tr>
              <th>Brend</th>
              <th>Roba</th>
              <th class="right">Trenutno (kom)</th>
              <th class="right">Trenutno (paleta)</th>
              <th class="right">Trenutno (m²)</th>
            </tr>
          </thead>
          <tbody>
            ${filteredStatus
              .slice()
              .sort(
                (a, b) =>
                  a.brandName.localeCompare(b.brandName, 'sr') ||
                  a.productName.localeCompare(b.productName, 'sr')
              )
              .map(
                (r) => `<tr>
                  <td>${escapeHtml(r.brandName)}</td>
                  <td>${escapeHtml(r.productName)}</td>
                  <td class="right">${escapeHtml(formatM2(r.currentKom))}</td>
                  <td class="right">${escapeHtml(formatM2(r.currentPaleta))}</td>
                  <td class="right">${escapeHtml(formatM2(r.currentM2))}</td>
                </tr>`
              )
              .join('')}
          </tbody>
        </table>`;
      }
    }

    const titleDates =
      needsDateRange && fromDate && toDate
        ? `${dayjs(fromDate).format('YYYYMMDD')}-${dayjs(toDate).format('YYYYMMDD')}`
        : `stanje-${dayjs().format('YYYYMMDD')}`;
    const title = `Izvestaj-Stanje-Robe-${titleDates}`;
    const ok = openPrintWindow(title, body);
    if (!ok) {
      setError('Browser je blokirao popup. Dozvolite popups za ovaj sajt pa pokušajte ponovo.');
    }
  };

  return (
    <div className="p-4">
      <h2 className="m-0 mb-4">Izveštaji - Stanje robe</h2>

      {error && <Message severity="error" text={error} className="mb-3" />}

      <Card>
        <div className="grid">
          {reportType !== 'current_status' && (
            <>
              <div className="col-12 md:col-6">
                <div className="field">
                  <label className="block mb-2">Početni datum</label>
                  <Calendar
                    value={fromDate}
                    onChange={(e) => setFromDate(e.value as Date)}
                    placeholder="Izaberite početni datum"
                    dateFormat="dd.mm.yy"
                    showIcon
                    appendTo={appendToBody}
                    className="w-full"
                  />
                </div>
              </div>
              <div className="col-12 md:col-6">
                <div className="field">
                  <label className="block mb-2">Završni datum</label>
                  <Calendar
                    value={toDate}
                    onChange={(e) => setToDate(e.value as Date)}
                    placeholder="Izaberite završni datum"
                    dateFormat="dd.mm.yy"
                    showIcon
                    appendTo={appendToBody}
                    className="w-full"
                  />
                </div>
              </div>
            </>
          )}

          <div className="col-12 md:col-6">
            <div className="field">
              <label className="block mb-2">Brendovi</label>
              <MultiSelect
                value={selectedBrandIds}
                options={brandOptions}
                onChange={(e) => {
                  setSelectedBrandIds(e.value || []);
                  setSelectedProductIds([]);
                }}
                placeholder="Svi brendovi"
                display="chip"
                filter
                appendTo={appendToBody}
                className="w-full"
              />
            </div>
          </div>

          <div className="col-12 md:col-6">
            <div className="field">
              <label className="block mb-2">Roba</label>
              <MultiSelect
                value={selectedProductIds}
                options={productOptions}
                onChange={(e) => setSelectedProductIds(e.value || [])}
                placeholder="Sva roba"
                display="chip"
                filter
                appendTo={appendToBody}
                className="w-full"
              />
              <small className="text-600">
                Prazno = svi brendovi / sva roba. Roba se filtrira prema izabranim brendovima.
              </small>
            </div>
          </div>

          <div className="col-12">
            <div className="field">
              <label className="block mb-2">Tip izveštaja</label>
              <div className="flex flex-wrap gap-4">
                <div className="flex align-items-center gap-2">
                  <RadioButton inputId="sr_all" value="all" checked={reportType === 'all'} onChange={(e) => setReportType(e.value)} />
                  <label htmlFor="sr_all">Sve (ulaz, izlaz, trenutno stanje)</label>
                </div>
                <div className="flex align-items-center gap-2">
                  <RadioButton inputId="sr_in" value="stock_in" checked={reportType === 'stock_in'} onChange={(e) => setReportType(e.value)} />
                  <label htmlFor="sr_in">Samo ulaz na stanje</label>
                </div>
                <div className="flex align-items-center gap-2">
                  <RadioButton inputId="sr_out" value="stock_out" checked={reportType === 'stock_out'} onChange={(e) => setReportType(e.value)} />
                  <label htmlFor="sr_out">Samo skidanje / isporuke</label>
                </div>
                <div className="flex align-items-center gap-2">
                  <RadioButton
                    inputId="sr_status"
                    value="current_status"
                    checked={reportType === 'current_status'}
                    onChange={(e) => setReportType(e.value)}
                  />
                  <label htmlFor="sr_status">Samo trenutno stanje</label>
                </div>
              </div>
              {reportType === 'current_status' && (
                <small className="text-600 block mt-2">
                  Trenutno stanje je snimak u trenutku generisanja izveštaja (bez vremenskog perioda).
                </small>
              )}
            </div>
          </div>

          <div className="col-12">
            <Button
              label="Generiši PDF"
              icon="pi pi-file-pdf"
              className="w-full"
              onClick={handleGenerate}
              loading={loading}
              severity="info"
            />
          </div>
        </div>
      </Card>
    </div>
  );
};

export default StanjeRobeReports;
